/**
 * @author Rob van den Hout <vdhout@studyportals.eu>
 * @author Danko Adamczyk <danko@studyportals.com>
 * @author Iliq Nikushev <iliq@studyportals.com>
 * @author Stanley Clark <stanley@studyportals.com>
 * @version 2.3.1
 * @copyright © 2014-2017 StudyPortals B.V., all rights reserved.
 */
import Shared from './Shared.js';
import { FetchQueue } from './FetchQueue.js';
import Parameters from './Parameters.js';

export class ServiceLayerClient {
	/**
	 * GET wrapper around a request to the Reflector.
	 *
	 * The settings object can contain a property called extra, this will
	 * contain any start, length, order and direction settings.
	 *
	 * The params property of settings is expected to contain un-aliased
	 * parameters. This function will alias them for you.
	 *
	 * It should also contain a path property that specifies the path that
	 * will be used to query the ServiceLayer.
	 *
	 * It will get a token before doing the actual request to the Reflector.
	 *
	 * @param {object} settings
	 * @param {Object} [settings.params]
	 * @param {Boolean} [settings.include_order]
	 * @param {Array} [settings.headers]
	 *
	 * @see ServiceLayerClient._getToken
	 * @see ServiceLayerClient._requestSL
	 */

	get(settings) {
		settings.method = 'get';
		settings.data = settings.extra || {};
		settings.data.include_order = !!settings.include_order;

		return this._getToken(settings).then((token) => {
			settings.data.token = token;
			if (settings.params) {
				settings.data.q = Parameters.alias(settings.params);
			}
			settings.data.path = settings.path;

			// Adding headers to get parameters to make sure caching will
			// take these headers into account.

			let vary_headers = ['X-StudyPortals-Brand', 'Accept-Language'];

			let vary_strings = [];

			if (settings.headers) {
				for (let i = 0; i < settings.headers.length; i++) {
					const header = settings.headers[i];

					if (vary_headers.indexOf(header.name) !== -1) {
						vary_strings.push(header.value);
						continue;
					}

					if (header.name === 'X-StudyPortals-Tally') {
						settings.data['tally'] = header.value;
					}
				}
			}

			if (vary_strings.length > 0) {
				settings.data['hh'] = vary_strings.join('-');
			}

			return this._requestSL(settings);
		});
	}

	/**
	 * GetAll functionality for the Reflector calls.
	 *
	 * After the initial request to this.get it will do subsequent this.get
	 * until all of the records have been returned from the ServiceLayer.
	 *
	 * It will concatenate every response to the already built response to
	 * create one big JSON document. This is to prevent any decoding and
	 * encoding to mess with the order of the elements.
	 *
	 * @see this.get for more information on the settings object.
	 *
	 * @param {object} settings
	 */

	getAll(settings) {
		let start = 0;

		let response;

		// Cloning the supplied onSuccess function to be able to substitute
		// our own.

		const callback = settings.onSuccess.clone();

		settings.onSuccess = (json, meta) => {
			// Removing last '}' from the original response and removing the
			// first '{' from the new response to be able to concatenate
			// them with a single comma ','.

			_: {
				if (response) {
					const orig = response.substring(0, response.length - 1);
					const add = json.substring(1, json.length);
					response = orig + ', ' + add;

					break _;
				}

				response = json;
			}

			// Upping start with the current response length

			start += parseInt(meta.length, 10);

			// If we have not retrieved all possible results.

			if (meta.total > start) {
				// Set the start for the settings object to the new value

				settings.extra.start = start;

				// Resetting any retry property to enable time-outs to retry
				delete settings.retry;
				this.get(settings);
			}

			// Call the original onSuccess function with the full
			// response and meta from the last request.
			callback(response, meta);
		};

		settings.extra = settings.extra || {};
		settings.extra.length = 0;
		settings.fixed = true;

		this.get(settings);
	}

	/**
	 * POST wrapper around a request to the Reflector.
	 *
	 * The settings object has to contain a params property containing all
	 * params you want to send to the ServiceLayer. It should also contain a
	 * path property that specifies the path that will be used to query the
	 * ServiceLayer.
	 *
	 * It will get a token before doing the actual request to the Reflector.
	 *
	 * @see this._requestSL and this._getToken for more information on the
	 * settings object.
	 *
	 * @param {object} settings
	 * @param {Object} settings.params
	 * @param {String} settings.path
	 *
	 * @see ServiceLayerClient._getToken
	 * @see ServiceLayerClient._requestSL
	 */

	post(settings) {
		settings.method = 'post';
		settings.data = {};

		return this._getToken(settings)
			.then((token) => {
				settings.data.token = token;
				settings.data.params = settings.params;
				settings.data.path = settings.path;
				return settings;
			})
			.then(this._requestSL);
	}

	/**
	 * PUT wrapper around a request to the Reflector.
	 *
	 * The settings object has to contain a params property containing all
	 * params you want to send to the ServiceLayer. It should also contain a
	 * path property that specifies the path that will be used to query the
	 * ServiceLayer.
	 *
	 * It will get a token before doing the actual request to the Reflector.
	 *
	 * @param {object} settings
	 * @param {String} settings.path
	 * @param {Object} settings.params
	 * @param {String} [settings.params.name]
	 *
	 * @see ServiceLayerClient._getToken
	 * @see ServiceLayerClient._requestSL
	 */

	put(settings) {
		settings.method = 'put';
		settings.data = {};

		return this._getToken(settings)
			.then((token) => {
				settings.data.token = token;
				settings.data.params = settings.params;
				if (settings.params.name) {
					settings.data.name = settings.params.name;
				}
				settings.data.path = settings.path;
				return settings;
			})
			.then(this._requestSL);
	}

	/**
	 * DELETE wrapper around a request to the Reflector.
	 *
	 * The settings object has to contain a params property containing all
	 * params you want to send to the ServiceLayer. It should also contain a
	 * path property that specifies the path that will be used to query the
	 * ServiceLayer.
	 *
	 * It will get a token before doing the actual request to the Reflector.
	 *
	 * @see this._requestSL and this._getToken  for more information on the
	 * settings object.
	 *
	 * @param {object} settings
	 * @see ServiceLayerClient._getToken
	 * @see ServiceLayerClient._requestSL
	 */

	remove(settings) {
		settings.method = 'delete';
		settings.data = {};

		return this._getToken(settings)
			.then((token) => {
				settings.data.token = token;
				settings.data.params = settings.params;
				settings.data.path = settings.path;
				return settings;
			})
			.then(this._requestSL);
	}

	/**
	 * Cancel the last request.
	 *
	 * This function cancels the request it's currently processing, if any.
	 * Queries for tokens will not be canceled, only requests send by the
	 * _requestSL function.
	 *
	 * @return void
	 */

	cancel() {}

	/**
	 * Try to retrieve a token from the sessionStorage or request a new one.
	 *
	 * @param {object} settings
	 * @return Promise
	 */

	_getToken(settings) {
		if (settings.token) {
			return Promise.resolve(settings.token);
		}

		const key = settings.method + ':' + settings.path;
		const token = Loot.DataStorage.retrieve(key);

		if (token) {
			return Promise.resolve(token);
		}

		// If specific module configuration is configured, use the module
		// to get the token

		if (typeof settings.sl_token === 'object') {
			return ServiceLayerClient._requestCustomToken(settings.sl_token).then((new_token) => {
				Loot.DataStorage.store(key, new_token);
				return new_token;
			});
		}

		// Get a reflector token using the Sentinel.php entry point
		if (settings.sentinel === true) {
			return ServiceLayerClient._requestTokenFromSentinel(settings.path).then((new_token) => {
				Loot.DataStorage.store(key, new_token);
				return new_token;
			});
		}

		// Get a token token directly from auth/reflector/

		return ServiceLayerClient._requestReflectorToken(settings.path).then((new_token) => {
			Loot.DataStorage.store(key, new_token);
			return new_token;
		});
	}

	/**
	 * Delete an invalid token.
	 *
	 * @param {object} settings
	 * @private
	 */

	static _deleteToken(settings) {
		return Loot.DataStorage.remove(settings.method + ':' + settings.path);
	}

	/**
	 * Get a token from the Auth driver and do the request.
	 *
	 * @param {String} path
	 * @return Promise
	 * @private
	 */

	static _requestReflectorToken(path) {
		let slUrl = authService.getServiceLayerUrl();
		let url = `${slUrl}auth/reflector/?url=${slUrl}${path}`;

		return FetchQueue.addRequest(data.url, url).then((response) => response.text());
	}

	/**
	 * Get a token from the Auth driver and do the request.
	 *
	 * @param {object} settings
	 * @return Promise
	 * @private
	 */

	static _requestCustomToken(settings) {
		let data = {
			method: 'get',
			url: authService.getServiceLayerUrl() + settings.path
		};

		if (settings.cookies) {
			data.credentials = 'include';
		}

		return FetchQueue.addRequest(data.url, data).then((response) => response.text());
	}

	/**
	 * Get a token from the Sentinel.php endpoint and do the request.
	 *
	 * @param {String} path
	 * @return Promise
	 * @private
	 */

	static _requestTokenFromSentinel(path) {
		let data = {
			method: 'get',
			url: Shared.getBaseUrl() + 'Sentinel.php?path=' + path
		};

		return FetchQueue.addRequest(data.url, data).then((response) => response.text());
	}

	/**
	 * Parse all headers for a Request to return the important properties.
	 *
	 * <p>It will look for the X-StudyPortals-Limit header to find start,
	 * length and max values for the current search. The
	 * X-StudyPortals-Total header to get the total for the current search.
	 * It will also check all response headers to find returned tally values
	 * and fill the tallies object.</p>
	 *
	 * @param {Headers} headers
	 * @returns {Object}
	 */

	static parseHeaders(headers) {
		let meta = {};

		// Parse the Limit header to fill the start, length and max values

		if (headers.has('X-StudyPortals-Limit')) {
			let limit_header = headers.get('X-StudyPortals-Limit');
			let limits = limit_header.split(',');

			meta.start = limits[0];
			meta.length = limits[1];
			meta.max = limits[2];
		}

		// Parse the Total header to fill the total value

		if (headers.has('X-StudyPortals-Total')) {
			meta.total = headers.get('X-StudyPortals-Total');
		}

		// Loop through all headers to find all the Tally headers

		let entries;

		try {
			entries = headers.entries();
		} catch (exc) {
			entries = [];
		}

		for (let i = 0; i < entries.length; i++) {
			let pair = entries[i];

			let name = pair[0].toLowerCase();
			let value = pair[1].toLowerCase();

			if (
				name.indexOf('x-studyportals-tally') !== -1 &&
				name.indexOf('vary') === -1 &&
				name.indexOf('access-control-allow-headers') === -1
			) {
				meta.tallies = meta.tallies || {};

				let X_StudyPortals_Tally_Name = name.split('-');
				let tally_name = X_StudyPortals_Tally_Name[3];

				meta.tallies[tally_name] = meta.tallies[tally_name] || {};

				let decoded_value = JSON.parse(value);

				for (let id in decoded_value) {
					if (decoded_value.hasOwnProperty(id)) {
						meta.tallies[tally_name][id] = decoded_value[id];
					}
				}
			}
		}

		return meta;
	}

	/**
	 * Do the actual request.
	 *
	 * <p>The supplied settings object is can to contain the following
	 * properties:</p>
	 *
	 * <ul>
	 *     <li>method: GET | POST | PUT | DELETE</li>
	 *     <li>data: the actual data being sent to the Reflector</li>
	 *     <li>onSuccess: a function that will be called with response and
	 *     meta arguments</li>
	 *     <li>onFailure: a function that will be called when every attempt
	 *     to get a proper response has failed. It will have retried on
	 *     time-out and have requested a new token on bad token.</li>
	 *     <li>headers: this needs to be an array of objects. Every object
	 *     should contain a name and value property</li>
	 * </ul>
	 *
	 * <p>This will retry once if the token is not valid or when it receives
	 * a time-out.</p>
	 *
	 * @param {object} settings
	 * @private
	 * @return Promise
	 */

	_requestSL(settings) {
		if (isNaN(settings.retry)) {
			settings.retry = 0;
		}

		let data = {
			method: settings.method
		};

		let headers = new Headers();

		// Set default language and brand headers, these will be overwritten
		// by custom headers in the settings object.
		// This is done to make sure browsers don't build their own language
		// headers and mess up caching.
		headers.append('Accept-Language', 'en-GB');
		headers.append('X-StudyPortals-Brand', 'default');

		// Add any headers that have been supplied.

		if (settings.headers) {
			settings.headers.forEach((header) => {
				headers.append(header.name, header.value);

				if (header.name === 'cache-control' && header.value === 'no-cache') {
					data.cache = 'no-cache';
				}
			});
		}

		data.headers = headers;

		let url = Meta.reflector_url;

		switch (settings.method.toLowerCase()) {
			case 'get':
				const enc = encodeURIComponent;
				url +=
					'?' +
					Object.keys(settings.data)
						.map((x) => {
							return enc(x) + '=' + enc(settings.data[x]);
						})
						.join('&');

				break;

			case 'post':
			case 'put':
			case 'delete':
				data.headers.append('content-type', 'application/x-www-form-urlencoded; charset=UTF-8');
				data.body = ServiceLayerClient.parseBody(settings.data, '');
				break;

			default:
				break;
		}

		let meta = {};

		return FetchQueue.addRequest(url, data)
			.then((response) => {
				if (response.status >= 300) {
					throw response;
				}

				meta = ServiceLayerClient.parseHeaders(response.headers);
				return response;
			})
			.then((response) => response.text())
			.then((body) => {
				if (typeof settings.onSuccess === 'function') {
					settings.onSuccess(body, meta);
				}

				return { body: body, meta: meta };
			})
			.catch((response) => {
				if (response.status === 401 && settings.retry < 1) {
					ServiceLayerClient._deleteToken(settings);
					return this._getToken(settings).then((token) => {
						settings.data.token = token;
						settings.retry++;

						return this._requestSL(settings);
					});
				}

				if (response.status === 503 && settings.retry < 3) {
					settings.retry++;

					return new Promise((resolve, reject) => {
						setTimeout(() => {
							this._requestSL(settings).then(resolve).catch(reject);
						}, 1000 * settings.retry);
					});
				}

				if (typeof settings.onFailure === 'function') {
					//as of 2.3.0 the error code is passed to be handled more specifically
					settings.onFailure(response.status, response);
				}

				throw response;
			});
	}

	static parseBody(data) {
		let body = [];
		const buildArray = (obj, path) => {
			for (let key in obj) {
				if (!obj.hasOwnProperty(key)) {
					continue;
				}

				const kPath = path === '' ? encodeURIComponent(key) : `[${encodeURIComponent(key)}]`;
				const val = obj[key];

				if (typeof val === 'object') {
					buildArray(val, `${path}${kPath}`);
					continue;
				}

				body.push(`${path}${kPath}=${encodeURIComponent(val)}`);
			}
		};

		buildArray(data, '');
		return body.join('&');
	}
}

window.ServiceLayerClient = ServiceLayerClient;
